import { useState } from 'react';
import type { OrderComment } from '@/infrastructure/FulfillmentAdminClient';
import { MentionWarning } from '@/pages/fulfillment/components/MentionWarning';
import { isComposingKey, useMentionSuggest } from '@/pages/fulfillment/components/MentionSuggest';
import { CommentPinButton, CommentStatusChip, CommentStatusPicker } from '@/pages/fulfillment/components/CommentStatus';
import { pinnedFirst, type OrderCommentStatus } from '@/models/OrderCommentStatus';
import { logTimestamp } from '../activityLog';

import { withMentions } from '@/pages/fulfillment/components/mentionText';

/** Shared with the memo row under the first order line, so a mention reads the same in both places [SSD-7216]. */
export { withMentions };

export interface OperatorCommentsProps {
  comments: OrderComment[];
  busy: boolean;
  /** Resolves true when the note was saved; on false the draft and its status stay so the desk can retry. */
  onPost: (text: string, status?: OrderCommentStatus) => Promise<boolean>;
  /** Pins a note to the top of the thread or takes the pin off (SSD-7609); without it the thread shows no pin control. */
  onPin?: (at: string, pinned: boolean) => Promise<void>;
}

/**
 * The order's own thread, open with the order rather than hidden behind its button. A note can carry a status beside its
 * text, and the order's one pinned note reads first (SSD-7609).
 */
export function OperatorComments({ comments, busy, onPost, onPin }: OperatorCommentsProps) {
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState<OrderCommentStatus | ''>('');
  const mention = useMentionSuggest<HTMLInputElement>({ value: draft, onChange: setDraft });

  const post = async () => {
    const text = draft.trim();
    if (!text || busy) {
      return;
    }
    // A plain note posts as it always did; only a note with a status carries one.
    const saved = await (status === '' ? onPost(text) : onPost(text, status));
    // A refused post keeps the words and the status for a retry (SSD-7609).
    if (saved) {
      setDraft('');
      setStatus('');
    }
  };

  return (
    <div className="cpanel">
      {comments.length === 0 && <div className="empty">No comments on this order yet.</div>}
      {pinnedFirst(comments).map((comment) => (
        <div
          className={`c-item${comment.pinned ? ' c-pinned' : ''}`}
          key={`${comment.at}-${comment.author}-${comment.text}`}
        >
          <span className="who">{comment.author}</span>
          {comment.status && <CommentStatusChip status={comment.status} />}
          <span>{withMentions(comment.text)}</span>
          <time>{logTimestamp(comment.at)}</time>
          {onPin && (
            <CommentPinButton
              pinned={comment.pinned === true}
              text={comment.text}
              disabled={busy}
              onToggle={() => void onPin(comment.at, comment.pinned !== true)}
            />
          )}
        </div>
      ))}
      <div className="cwrite">
        <CommentStatusPicker value={status} onChange={setStatus} disabled={busy} />
        <input
          {...mention.fieldProps}
          aria-label="Write a comment"
          placeholder="Write a comment — @name sends an automatic Slack alert (order no · text · time · author)"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (mention.onKeyDown(event)) {
              return;
            }
            // The Enter that confirms a Korean syllable is not a post.
            if (event.key === 'Enter' && !isComposingKey(event)) {
              event.preventDefault();
              void post();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-blue btn-sm"
          disabled={busy || !draft.trim()}
          onClick={() => void post()}
        >
          Post
        </button>
      </div>
      {mention.list}
      <MentionWarning text={draft} typing={mention.typing} />
    </div>
  );
}
